/**
 * SoldPriceModal — Captures actual sale price before marking artwork as sold.
 * v9.0: Added Flow C match suggestion step after entering sale details.
 *
 * Step 1: Capture sale details (price, note, date, buyer)
 * Step 2: If matching bank deposits found, show match candidates for linking
 */

import { useState, useEffect, useRef } from 'react';
import { T } from '@/styles/tokens';
import { findMatchCandidates, type MatchCandidate } from '@/services/matchSuggestion';

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  height: 44,
  borderRadius: T.radiusMd,
  border: `1.5px solid ${T.border}`,
  padding: `0 ${T.lg}`,
  fontSize: 14,
  fontWeight: 400,
  color: T.text,
  fontFamily: T.fontFamily,
  outline: 'none',
  background: T.white,
  boxSizing: 'border-box',
};

interface SoldPriceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (soldPrice: number, soldNote?: string, soldDate?: string, soldBuyer?: string, linkedTransactionId?: string | null) => Promise<void>;
  artworkTitle: string;
  askingPrice: number;    // cents — pre-fill value
  isSaving: boolean;
  userId?: string;        // needed for match candidate query
}

export function SoldPriceModal({
  isOpen,
  onClose,
  onConfirm,
  artworkTitle,
  askingPrice,
  isSaving,
  userId,
}: SoldPriceModalProps) {
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [priceStr, setPriceStr] = useState('');
  const [note, setNote] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [soldDateStr, setSoldDateStr] = useState('');
  const [buyer, setBuyer] = useState('');
  const priceRef = useRef<HTMLInputElement>(null);

  // Match step state
  const [step, setStep] = useState<'input' | 'match'>('input');
  const [isSearching, setIsSearching] = useState(false);
  const [candidates, setCandidates] = useState<MatchCandidate[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<string | null>(null); // txnId or 'none'

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      const dollars = Math.round(askingPrice / 100);
      setPriceStr(dollars > 0 ? String(dollars) : '');
      setNote('');
      setShowDetails(false);
      setSoldDateStr('');
      setBuyer('');
      setStep('input');
      setCandidates([]);
      setSelectedMatch(null);
      setIsSearching(false);
      setTimeout(() => priceRef.current?.select(), 50);
    }
  }, [isOpen, askingPrice]);

  const parsedPrice = parseFloat(priceStr.replace(/[^0-9.]/g, ''));
  const isValid = !isNaN(parsedPrice) && parsedPrice > 0;

  const handleClose = () => {
    if (isSaving || isSearching) return;
    setIsAnimatingOut(true);
    setTimeout(() => {
      onClose();
      setIsAnimatingOut(false);
    }, 200);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving && !isSearching) {
      handleClose();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = T.primary;
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = T.border;
  };

  // Step 1 → check for matches → Step 2 or direct save
  const handleConfirmSale = async () => {
    if (!isValid || isSaving) return;
    const priceCents = Math.round(parsedPrice * 100);
    const isoDate = soldDateStr ? new Date(soldDateStr + 'T12:00:00').toISOString() : undefined;
    const dateForQuery = isoDate || new Date().toISOString();

    // If no userId (not connected to bank), skip match step
    if (!userId) {
      await onConfirm(priceCents, note.trim() || undefined, isoDate, buyer.trim() || undefined, null);
      return;
    }

    // Search for matching deposits
    setIsSearching(true);
    try {
      const matches = await findMatchCandidates(userId, priceCents, dateForQuery);
      if (matches.length === 0) {
        // No matches — save as Pool 2 (reported)
        await onConfirm(priceCents, note.trim() || undefined, isoDate, buyer.trim() || undefined, null);
      } else {
        // Show match step
        setCandidates(matches);
        setSelectedMatch(matches[0].transactionId); // pre-select best match
        setStep('match');
      }
    } catch {
      // Query failed — fall back to saving without match
      await onConfirm(priceCents, note.trim() || undefined, isoDate, buyer.trim() || undefined, null);
    } finally {
      setIsSearching(false);
    }
  };

  // Step 2 → link & save
  const handleLinkAndSave = async () => {
    if (isSaving) return;
    const priceCents = Math.round(parsedPrice * 100);
    const isoDate = soldDateStr ? new Date(soldDateStr + 'T12:00:00').toISOString() : undefined;
    const linkedTxnId = selectedMatch === 'none' ? null : (selectedMatch || null);
    await onConfirm(priceCents, note.trim() || undefined, isoDate, buyer.trim() || undefined, linkedTxnId);
  };

  if (!isOpen && !isAnimatingOut) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-200 ${
        isAnimatingOut ? 'bg-black/0' : 'bg-black/50'
      }`}
      style={{ backdropFilter: isAnimatingOut ? 'blur(0px)' : 'blur(4px)' }}
      onClick={handleBackdropClick}
    >
      <div
        className={`w-full max-w-sm rounded-2xl shadow-xl transition-all duration-300 ease-out ${
          isAnimatingOut ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{
          backgroundColor: T.cream,
          animation: !isAnimatingOut ? `scaleInModal 0.3s ${T.easeEntrance}` : 'none',
        }}
      >
        {step === 'input' ? (
          <>
            <div style={{ padding: T['2xl'], textAlign: 'center' }}>
              {/* Teal icon */}
              <div style={{
                width: 64,
                height: 64,
                margin: `0 auto ${T.lg}`,
                borderRadius: '50%',
                background: `${T.primary}1A`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
              </div>

              <h2 style={{
                fontSize: 18, fontWeight: 700, color: T.text,
                margin: `0 0 ${T.sm}`, fontFamily: T.fontFamily,
              }}>
                Record this sale
              </h2>
              <p style={{
                fontSize: 14, fontWeight: 400, color: T.textSecondary,
                margin: 0, fontFamily: T.fontFamily,
              }}>
                What did &ldquo;{artworkTitle}&rdquo; actually sell for?
              </p>
            </div>

            {/* Price input */}
            <div style={{ padding: `0 ${T['2xl']} ${T.md}` }}>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: 16, top: '50%',
                  transform: 'translateY(-50%)', fontSize: 24, fontWeight: 400,
                  color: T.textMuted, fontFamily: T.fontFamily, pointerEvents: 'none',
                }}>$</span>
                <input
                  ref={priceRef}
                  type="text"
                  inputMode="numeric"
                  value={priceStr}
                  onChange={(e) => setPriceStr(e.target.value)}
                  autoFocus
                  style={{
                    width: '100%', height: 52, borderRadius: T.radiusMd,
                    border: `1.5px solid ${T.border}`, paddingLeft: 36, paddingRight: 16,
                    fontSize: 24, fontWeight: 700, color: T.text,
                    fontFamily: T.fontFamily, outline: 'none', background: T.white,
                    boxSizing: 'border-box',
                  }}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
              </div>
            </div>

            {/* Note input */}
            <div style={{ padding: `0 ${T['2xl']} ${T.md}` }}>
              <input
                type="text" value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note (optional)" style={INPUT_STYLE}
                onFocus={handleFocus} onBlur={handleBlur}
              />
            </div>

            {/* Add details toggle */}
            <button
              onClick={() => setShowDetails(v => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: T.fontFamily, fontSize: 13, fontWeight: 500,
                color: T.primary, padding: `0 ${T['2xl']} ${T.lg}`, display: 'block',
              }}
            >
              {showDetails ? 'Hide details' : 'Add details'}
            </button>

            {/* Expandable details */}
            {showDetails && (
              <>
                <div style={{ padding: `0 ${T['2xl']} ${T.md}` }}>
                  <label style={{
                    display: 'block', fontFamily: T.fontFamily, fontSize: 12,
                    fontWeight: 500, color: T.textSecondary, marginBottom: 4,
                  }}>Sale date</label>
                  <input
                    type="date" value={soldDateStr}
                    onChange={(e) => setSoldDateStr(e.target.value)}
                    style={INPUT_STYLE} onFocus={handleFocus} onBlur={handleBlur}
                  />
                </div>
                <div style={{ padding: `0 ${T['2xl']} ${T.lg}` }}>
                  <input
                    type="text" value={buyer}
                    onChange={(e) => setBuyer(e.target.value)}
                    placeholder="Buyer name (optional)" style={INPUT_STYLE}
                    onFocus={handleFocus} onBlur={handleBlur}
                  />
                </div>
              </>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: `0 ${T['2xl']} ${T['2xl']}` }}>
              <button
                onClick={handleConfirmSale}
                disabled={!isValid || isSaving || isSearching}
                style={{
                  width: '100%', height: 48, borderRadius: 24, border: 'none',
                  background: isValid && !isSaving && !isSearching ? T.primary : T.border,
                  color: isValid && !isSaving && !isSearching ? T.white : T.textMuted,
                  fontSize: 15, fontWeight: 600, fontFamily: T.fontFamily,
                  cursor: isValid && !isSaving && !isSearching ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'background 0.15s ease, color 0.15s ease',
                }}
              >
                {isSaving || isSearching ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                      <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {isSearching ? 'Checking for deposits...' : 'Saving...'}
                  </>
                ) : (
                  'Confirm Sale'
                )}
              </button>
              <button
                onClick={handleClose}
                disabled={isSaving || isSearching}
                style={{
                  width: '100%', height: 48, borderRadius: 24,
                  border: `1.5px solid ${T.border}`, background: 'transparent',
                  color: T.text, fontSize: 15, fontWeight: 600, fontFamily: T.fontFamily,
                  cursor: isSaving || isSearching ? 'default' : 'pointer',
                  opacity: isSaving || isSearching ? 0.5 : 1,
                  transition: 'opacity 0.15s ease',
                }}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          /* Step 2: Match suggestion */
          <>
            <div style={{ padding: T['2xl'], textAlign: 'center' }}>
              <h2 style={{
                fontSize: 18, fontWeight: 700, color: T.text,
                margin: `0 0 ${T.sm}`, fontFamily: T.fontFamily,
              }}>
                We found a matching deposit
              </h2>
              <p style={{
                fontSize: 14, fontWeight: 400, color: T.textSecondary,
                margin: 0, fontFamily: T.fontFamily,
              }}>
                Link this sale to a bank deposit for accurate tracking.
              </p>
            </div>

            <div style={{ padding: `0 ${T['2xl']}`, display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '40vh', overflowY: 'auto' }}>
              {candidates.map((c) => {
                const isSelected = selectedMatch === c.transactionId;
                const dollars = Math.round(c.amount / 100);
                return (
                  <button
                    key={c.transactionId}
                    onClick={() => setSelectedMatch(c.transactionId)}
                    style={{
                      width: '100%', padding: 16, borderRadius: 12,
                      border: `1.5px solid ${isSelected ? T.primary : T.border}`,
                      background: isSelected ? 'rgba(61,171,165,0.06)' : T.white,
                      cursor: 'pointer', textAlign: 'left',
                      display: 'flex', alignItems: 'center', gap: 12,
                      transition: 'border-color 150ms, background 150ms',
                    }}
                  >
                    {/* Radio */}
                    <div style={{
                      width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                      border: `2px solid ${isSelected ? T.primary : T.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {isSelected && (
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: T.primary }} />
                      )}
                    </div>
                    {/* Details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 500, color: T.text, fontFamily: T.fontFamily }}>
                        ${dollars.toLocaleString()} · {c.merchantName}
                      </div>
                      <div style={{ fontSize: 13, color: T.textSecondary, fontFamily: T.fontFamily, marginTop: 2 }}>
                        {c.date} · {c.amountMatchPercent}% of sale price
                      </div>
                    </div>
                  </button>
                );
              })}

              {/* "None of these" option */}
              <button
                onClick={() => setSelectedMatch('none')}
                style={{
                  width: '100%', padding: 16, borderRadius: 12,
                  border: `1.5px ${selectedMatch === 'none' ? 'solid' : 'dashed'} ${selectedMatch === 'none' ? T.primary : T.border}`,
                  background: selectedMatch === 'none' ? 'rgba(61,171,165,0.06)' : T.white,
                  cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 12,
                  transition: 'border-color 150ms, background 150ms',
                }}
              >
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${selectedMatch === 'none' ? T.primary : T.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {selectedMatch === 'none' && (
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: T.primary }} />
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: T.text, fontFamily: T.fontFamily }}>
                    None of these
                  </div>
                </div>
              </button>
            </div>

            {/* Link & Save button */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: `${T.lg} ${T['2xl']} ${T['2xl']}` }}>
              <button
                onClick={handleLinkAndSave}
                disabled={!selectedMatch || isSaving}
                style={{
                  width: '100%', height: 48, borderRadius: 24, border: 'none',
                  background: selectedMatch && !isSaving ? T.primary : T.border,
                  color: selectedMatch && !isSaving ? T.white : T.textMuted,
                  fontSize: 15, fontWeight: 600, fontFamily: T.fontFamily,
                  cursor: selectedMatch && !isSaving ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'background 0.15s ease, color 0.15s ease',
                }}
              >
                {isSaving ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
                      <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  selectedMatch === 'none' ? 'Save Without Linking' : 'Link & Save'
                )}
              </button>
              <button
                onClick={() => setStep('input')}
                disabled={isSaving}
                style={{
                  width: '100%', height: 48, borderRadius: 24,
                  border: `1.5px solid ${T.border}`, background: 'transparent',
                  color: T.text, fontSize: 15, fontWeight: 600, fontFamily: T.fontFamily,
                  cursor: isSaving ? 'default' : 'pointer',
                  opacity: isSaving ? 0.5 : 1,
                }}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes scaleInModal {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default SoldPriceModal;
